<template>
  <v-main>
    <v-container fluid>
      <!-- Header -->
      <v-row class="mt-4 ml-2 mr-2">
        <v-col cols="12">
          <v-card elevation="6" class="header-card">
            <v-card-text class="d-flex align-center justify-space-between py-3">
              <div class="d-flex align-center">
                <v-avatar color="teal" size="42" class="mr-3">
                  <v-icon dark>mdi-calendar-star</v-icon>
                </v-avatar>
                <div>
                  <div class="text-h5 font-weight-bold">Holidays</div>
                  <div class="grey--text text-caption">Manage company holidays</div>
                </div>
              </div>
              <v-btn color="teal" depressed dark @click="openDialog()">
                <v-icon left>mdi-plus</v-icon>
                Add Holiday
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Search -->
      <v-row class="ml-2 mr-2">
        <v-col cols="12">
          <v-card elevation="6">
            <v-card-text class="py-2">
              <v-row align="center">
                <v-col md="4">
                  <v-text-field v-model="search_keyword" label="Search holidays..." prepend-inner-icon="mdi-magnify"
                    outlined dense hide-details @keydown.enter="getData" @change="page = 1" clearable></v-text-field>
                </v-col>
                <v-col md="3" v-if="allCompanies.length > 0">
                  <v-autocomplete v-model="filter_company_id" :items="allCompanies" label="Company"
                    prepend-inner-icon="mdi-domain" outlined dense hide-details clearable
                    @input="getData"></v-autocomplete>
                </v-col>
                <v-spacer></v-spacer>
                <v-col md="4" class="d-flex justify-end">
                  <v-pagination v-model="page" :length="paginationLength" circle color="teal" :total-visible="5"></v-pagination>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Loading -->
      <v-progress-linear v-if="isLoading" indeterminate color="teal" class="mx-6"></v-progress-linear>

      <!-- Table -->
      <v-row class="ml-2 mr-2" v-if="!isLoading">
        <v-col cols="12">
          <v-card elevation="6">
            <v-simple-table v-if="holidays.length > 0">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Date</th>
                    <th>Day</th>
                    <th>Status</th>
                    <th class="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(holiday, i) in holidays" :key="holiday.id">
                    <td class="grey--text">{{ (page - 1) * rowsPerPage + i + 1 }}</td>
                    <td class="font-weight-medium">{{ holiday.name }}</td>
                    <td>{{ formatDate(holiday.date) }}</td>
                    <td>{{ formatDayName(holiday.date) }}</td>
                    <td>
                      <v-chip x-small :color="holiday.is_active ? 'teal' : 'grey'" dark>
                        {{ holiday.is_active ? 'Active' : 'Inactive' }}
                      </v-chip>
                    </td>
                    <td class="text-center">
                      <v-btn icon x-small @click="openDialog(holiday)" class="mr-1">
                        <v-icon small color="teal">mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn icon x-small @click="deleteHoliday(holiday.id)">
                        <v-icon small color="red lighten-1">mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <!-- Empty State -->
            <div v-else class="text-center py-10">
              <v-icon size="64" color="grey lighten-1">mdi-calendar-star</v-icon>
              <div class="text-h6 grey--text mt-3">No holidays found</div>
              <div class="grey--text text--lighten-1 mb-4">Add your first holiday to get started</div>
              <v-btn color="teal" depressed dark @click="openDialog()">
                <v-icon left>mdi-plus</v-icon>
                Add Holiday
              </v-btn>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Snackbar for feedback -->
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3500" top right>
        {{ snackbar.text }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
        </template>
      </v-snackbar>

      <!-- Add/Edit Dialog -->
      <v-dialog v-model="dialog" max-width="450" persistent>
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon color="teal" class="mr-2">mdi-calendar-star</v-icon>
            {{ editingId ? 'Edit Holiday' : 'Add Holiday' }}
          </v-card-title>
          <v-card-text>
            <v-form ref="form" v-model="valid">
              <v-text-field v-model="form.name" label="Holiday Name" outlined dense
                placeholder="e.g. Republic Day, Diwali"
                :rules="[rules.required]" :error-messages="errors.name" class="mb-2"></v-text-field>

              <v-menu v-model="date_menu" :close-on-content-click="false" transition="scale-transition"
                offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="form.date" label="Date" outlined dense readonly v-bind="attrs" v-on="on"
                    prepend-inner-icon="mdi-calendar"
                    :rules="[rules.required]" :error-messages="errors.date"></v-text-field>
                </template>
                <v-date-picker v-model="form.date" @input="date_menu = false" color="teal"></v-date-picker>
              </v-menu>

              <v-switch v-model="form.is_active" color="teal" class="mt-0"
                :label="form.is_active ? 'Active' : 'Inactive'" hide-details></v-switch>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="closeDialog">Cancel</v-btn>
            <v-btn color="teal" dark depressed @click="submit" :loading="isSaving" :disabled="!valid">
              {{ editingId ? 'Update' : 'Save' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      search_keyword: "",
      page: 1,
      paginationLength: 1,
      holidays: [],
      isLoading: true,
      isSaving: false,
      dialog: false,
      date_menu: false,
      editingId: null,
      valid: false,
      form: {
        name: "",
        date: "",
        is_active: true,
      },
      rules: {
        required: (value) => !!value || "Required.",
      },
      filter_company_id: "",
      allCompanies: [],
      snackbar: { show: false, text: "", color: "teal" },
    };
  },
  mounted() {
    this.getData();
    this.loadCompanies();
  },
  watch: {
    page() {
      this.getData();
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },
    formatDayName(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("en-IN", { weekday: "long" });
    },
    openDialog(holiday) {
      if (holiday) {
        this.editingId = holiday.id;
        this.form = {
          name: holiday.name,
          date: holiday.date ? holiday.date.substring(0, 10) : "",
          is_active: Boolean(holiday.is_active),
        };
      } else {
        this.editingId = null;
        this.form = { name: "", date: "", is_active: true };
      }
      this.dialog = true;
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },
    closeDialog() {
      this.dialog = false;
      this.editingId = null;
      this.form = { name: "", date: "", is_active: true };
    },
    async submit() {
      if (!this.$refs.form.validate()) return;
      this.isSaving = true;
      try {
        if (this.editingId) {
          await axios.patch(`/holidays/${this.editingId}`, {
            ...this.form,
            id: this.editingId,
          });
        } else {
          await axios.post("/holidays", this.form);
        }
        this.showSnackbar(this.editingId ? "Holiday updated" : "Holiday added", "teal");
        this.closeDialog();
        this.getData();
      } catch (error) {
        const resp = error.response && error.response.data;
        const message =
          (resp && (resp.message || (resp.errors && Object.values(resp.errors)[0]))) ||
          "Failed to save holiday";
        this.showSnackbar(Array.isArray(message) ? message[0] : message, "red");
      } finally {
        this.isSaving = false;
      }
    },
    showSnackbar(text, color) {
      this.snackbar = { show: true, text, color };
    },
    async getData() {
      this.isLoading = true;
      try {
        const response = await axios.get("/holidays", {
          params: {
            page: this.page,
            rowsPerPage: this.rowsPerPage,
            search_keyword: this.search_keyword,
            filter_company_id: this.filter_company_id || "",
          },
        });
        this.holidays = response.data.data;
        this.paginationLength = Math.ceil(response.data.count / this.rowsPerPage);
      } catch (error) {
        console.error("Error fetching holidays:", error);
      } finally {
        this.isLoading = false;
      }
    },
    async loadCompanies() {
      // SUPER ADMIN of the main company (id=1) OR any user with the "View All Employees" permission
      // is allowed to filter across companies.
      const isSuperAdminMain =
        this.companyId == 1 && this.user && this.user.roles && this.user.roles[0].name === "SUPER ADMIN";
      if (!isSuperAdminMain && !this.canViewAllEmployees) {
        return;
      }
      try {
        const response = await axios.get("/companies");
        const companies = response.data.data || [];
        this.allCompanies = [{ text: "All Companies", value: "all" }];
        companies.forEach((c) => {
          this.allCompanies.push({ text: c.name, value: c.id });
        });
      } catch (error) {
        console.error("Error loading companies:", error);
      }
    },
    async deleteHoliday(id) {
      if (confirm("Are you sure you want to delete this holiday?")) {
        try {
          await axios.post("/holidays/clear", { id: id });
          this.showSnackbar("Holiday deleted", "teal");
          this.getData();
        } catch (error) {
          const resp = error.response && error.response.data;
          this.showSnackbar((resp && resp.message) || "Failed to delete holiday", "red");
        }
      }
    },
  },
};
</script>

<style scoped>
.header-card {
  border-left: 4px solid #009688;
}
</style>
